import api from '../api'
import store from '../store'
import { fetchUser } from './user'
import {
  isEmailVerificationQAEnabled,
  getEmailVerificationQAState,
  setEmailVerificationQAState,
  derivePrompts,
  fakeVerificationRecord,
  reauthRequiredError,
  mockVerificationCodeError,
} from '../utils/emailVerificationQA'

export function fetchEmailVerificationPrompts(userId) {
  if (isEmailVerificationQAEnabled()) {
    return (dispatch) => {
      const prompts = derivePrompts(getEmailVerificationQAState())
      dispatch({ type: 'FETCH_EMAIL_VERIFICATION_PROMPTS_SUCCESS', response: prompts })
      return Promise.resolve(prompts)
    }
  }
  return (dispatch) => api.fetch(`/users/${userId}/email-verification/prompts`)
    .then((response) => {
      dispatch({ type: 'FETCH_EMAIL_VERIFICATION_PROMPTS_SUCCESS', response })
      return response
    })
    .catch(() => {})
}

// TEMP: the dismiss endpoint isn't live on the backend yet. Until it is, keep
// the dismiss timestamp local-only (redux state, not persisted) so the UI is
// testable without needing a matching DB write. Flip DISMISS_LOCALLY_ONLY back
// to false once the backend endpoint ships.
const DISMISS_LOCALLY_ONLY = true

// name = 'verifyPrimary' | 'affirmPrimary' | 'addBackup' | 'affirmBackup'
// data = { never?: true } — never: true means "don't ask me again" for this prompt
export function dismissEmailVerificationPrompt(userId, name, data) {
  if (isEmailVerificationQAEnabled()) {
    return (dispatch) => {
      if (name === 'verifyPrimary' || name === 'affirmPrimary') {
        setEmailVerificationQAState({ primaryState: 'none' })
      } else {
        // addBackup/affirmBackup are the same underlying 'due' toggle, just
        // read differently depending on qa.hasBackupEmail
        setEmailVerificationQAState({ backupState: 'none' })
      }
      dispatch({ type: 'DISMISS_EMAIL_VERIFICATION_PROMPT_SUCCESS', name })
      return Promise.resolve()
    }
  }
  if (DISMISS_LOCALLY_ONLY) {
    return (dispatch) => {
      dispatch({ type: 'DISMISS_EMAIL_VERIFICATION_PROMPT_SUCCESS', name })
      return Promise.resolve()
    }
  }
  return (dispatch) => api.put(`/users/${userId}/email-verification/prompts/${name}/dismiss`, data)
    .then((response) => {
      dispatch({ type: 'DISMISS_EMAIL_VERIFICATION_PROMPT_SUCCESS', name })
      return response
    })
}

// the create endpoint's upsertBackup purpose is now live — hooked up to the
// real API. Flip this back to true if it needs mocking again for UI-only work.
const MOCK_UPSERT_BACKUP_SUCCESS = false

// mirrors the real endpoint: verifyPrimary never needs reauth, updatePrimary/
// upsertBackup do, once per QA "session" (reset via the QA panel or a reload)
function qaCreateEmailVerification(data) {
  const qa = getEmailVerificationQAState()
  const reauthableCredential = data.type && (data.password || data.idToken)
  const needsReauth = data.purpose !== 'verifyPrimary' && qa.requireReauth &&
    !qa.reauthed && !reauthableCredential
  if (needsReauth) {
    return Promise.reject(reauthRequiredError())
  }
  if (reauthableCredential) {
    setEmailVerificationQAState({ reauthed: true })
  }
  return Promise.resolve(fakeVerificationRecord(data.purpose))
}

// data = { purpose: 'verifyPrimary' | 'updatePrimary' | 'upsertBackup', email?,
//          type?: 'password' | 'apple' | 'google', password?, idToken? }
// → { id, purpose, expiresAt, resendAvailableAt }
export function createEmailVerification(userId, data) {
  if (isEmailVerificationQAEnabled()) {
    return () => qaCreateEmailVerification(data)
  }
  if (MOCK_UPSERT_BACKUP_SUCCESS && data.purpose === 'upsertBackup') {
    return () => Promise.resolve(fakeVerificationRecord('upsertBackup'))
  }
  return () => api.post(`/users/${userId}/email-verification`, data)
    .then((response) => response)
}

export function resendEmailVerification(userId, verificationId) {
  if (isEmailVerificationQAEnabled()) {
    return () => Promise.resolve(fakeVerificationRecord('resend'))
  }
  return () => api.post(`/users/${userId}/email-verification/${verificationId}/resend`)
    .then((response) => response)
}

// TEMP: the affirm endpoint isn't live on the backend yet. Until it is, treat
// any affirm as an immediate success instead of hitting the API. Flip
// MOCK_AFFIRM_PRIMARY_SUCCESS back to false once the backend endpoint ships.
const MOCK_AFFIRM_PRIMARY_SUCCESS = true

// Affirms a previously-verified primary email whose confirmation has gone
// stale (the affirmPrimary prompt) — no code round trip. The caller should
// refetch prompts afterward to see whether addBackup is still due.
export function affirmPrimaryEmail(userId) {
  if (isEmailVerificationQAEnabled()) {
    return () => {
      setEmailVerificationQAState({ primaryState: 'none' })
      return Promise.resolve({ status: 'succeeded' })
    }
  }
  if (MOCK_AFFIRM_PRIMARY_SUCCESS) {
    return () => Promise.resolve({ status: 'succeeded' })
  }
  return (dispatch) => api.put(`/users/${userId}/email-verification/affirm-primary`)
    .then((response) => {
      // affirm updates meta.emailConfirmedAt, same as verify
      dispatch(fetchUser(userId))
      return response
    })
}

// TEMP: the affirm-backup endpoint isn't live on the backend yet. Until it
// is, treat any affirm as an immediate success instead of hitting the API.
// Flip MOCK_AFFIRM_BACKUP_SUCCESS back to false once the backend endpoint ships.
const MOCK_AFFIRM_BACKUP_SUCCESS = true

// Affirms a previously-verified backup email whose confirmation has gone
// stale (the affirmBackup prompt) — no code round trip, mirrors affirmPrimary.
export function affirmBackupEmail(userId) {
  if (isEmailVerificationQAEnabled()) {
    return () => {
      setEmailVerificationQAState({ backupState: 'none' })
      return Promise.resolve({ status: 'succeeded' })
    }
  }
  if (MOCK_AFFIRM_BACKUP_SUCCESS) {
    return () => Promise.resolve({ status: 'succeeded' })
  }
  return (dispatch) => api.put(`/users/${userId}/email-verification/affirm-backup`)
    .then((response) => {
      dispatch(fetchUser(userId))
      return response
    })
}

// TEMP: the verify endpoint isn't live on the backend yet for verifyPrimary /
// updatePrimary. Until it is, treat any submitted code as a success for those
// two purposes instead of hitting the API. upsertBackup is now hooked up to
// the real API (excluded here), so a real code is required for it.
// Flip MOCK_VERIFY_SUCCESS back to false once the backend endpoint ships.
const MOCK_VERIFY_SUCCESS = true

function qaVerifyEmailVerification(purpose) {
  let qa = getEmailVerificationQAState()
  if (purpose === 'verifyPrimary' || purpose === 'updatePrimary') {
    qa = setEmailVerificationQAState({ primaryState: 'none' })
  } else if (purpose === 'upsertBackup') {
    qa = setEmailVerificationQAState({ hasBackupEmail: true, backupState: 'none' })
  }
  return { status: 'succeeded', attemptsRemaining: null, prompts: derivePrompts(qa) }
}

// purpose: 'verifyPrimary' | 'updatePrimary' | 'upsertBackup'. email is only
// used to keep QA mode's mocked user.email/backupEmail in sync with what was
// actually typed into the form — real requests don't need it (verify is the
// only writer of those fields server-side, and a real fetchUser picks it up).
// → { status, attemptsRemaining, prompts }
export function verifyEmailVerification(userId, verificationId, code, purpose, email) {
  if (isEmailVerificationQAEnabled()) {
    return (dispatch) => {
      const codeError = mockVerificationCodeError(code)
      if (codeError) {
        return Promise.reject(codeError)
      }
      const response = qaVerifyEmailVerification(purpose)
      dispatch({ type: 'VERIFY_EMAIL_VERIFICATION_SUCCESS', response })
      if (email && (purpose === 'updatePrimary' || purpose === 'upsertBackup')) {
        const patch = purpose === 'updatePrimary' ? { email } : { backupEmail: email }
        dispatch({ type: 'UPDATE_USER_SUCCESS', response: { ...store.getState().user, ...patch } })
      }
      return Promise.resolve(response)
    }
  }
  if (MOCK_VERIFY_SUCCESS && purpose !== 'upsertBackup') {
    return (dispatch) => {
      const response = {
        status: 'succeeded',
        attemptsRemaining: null,
        prompts: { verifyPrimary: false, addBackup: purpose !== 'upsertBackup' },
      }
      dispatch({ type: 'VERIFY_EMAIL_VERIFICATION_SUCCESS', response })
      return Promise.resolve(response)
    }
  }
  return (dispatch) => api.put(`/users/${userId}/email-verification/${verificationId}/verify`, { code })
    .then((response) => {
      dispatch({ type: 'VERIFY_EMAIL_VERIFICATION_SUCCESS', response })
      // verify is the only writer of user.email / user.backupEmail / the confirm-verify
      // timestamps, so refresh the user record to pick up whatever changed.
      dispatch(fetchUser(userId))
      return response
    })
}
